import {
  calibrationTargetsMoved,
  isCalibrationCurrent,
  isValidCalibration,
  type CalibrationTargetPlan,
} from '@repo/front-end-utils';
import type { FloorPlannerCamera } from '@repo/frontend-types';
import { MIN_GUIDED_SPOTS, plural } from './calibrationModel';

/**
 * The camera panel's calibration status (`observer-autogen-plan-projection`, R1, R11; design card
 * "Camera Calibration", panel board): which state it is in, and the sentences for it. Pure, so each
 * state is tested without rendering.
 */
export type CalibrationStatusState =
  | 'no-stream'
  | 'no-picture'
  | 'not-calibrated'
  | 'calibrated'
  | 'out-of-date';

export interface CalibrationStatus {
  state: CalibrationStatusState;
  title: string;
  sub: string;
  /** The action's visible label: Calibrate or Recalibrate. */
  action: 'Calibrate' | 'Recalibrate';
  /** Calibrate is disabled (R1): no stream, or a stream with no picture yet. */
  disabled: boolean;
  /** Remove calibration is offered: there is one stored, current or not. */
  removable: boolean;
}

/** "13 Oct". */
const shortDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? 'earlier'
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/**
 * The fitted tiles' stored errors (`observer-autogen-calibration-camera-model`, R13): the worst floor
 * error in metres and the worst RMS in picture px, over the tiles that have them.
 */
const storedErrors = (calibration: NonNullable<FloorPlannerCamera['calibration']>) => {
  let worstM: number | null = null;
  let rmsPx: number | null = null;
  for (const view of calibration.views) {
    if (!view.camera) continue;
    if (view.worstErrorM !== null) worstM = Math.max(worstM ?? 0, view.worstErrorM);
    if (view.rmsErrorPx !== null) rmsPx = Math.max(rmsPx ?? 0, view.rmsErrorPx);
  }
  return { worstM, rmsPx };
};

const sourceWords: Record<NonNullable<FloorPlannerCamera['calibration']>['source'], string> = {
  clicked: 'clicked points',
  observers: 'points from observers',
  mixed: 'points, some from observers',
};

/**
 * The status for a camera. `hasPicture` is whether its stream has a picture to calibrate on;
 * `frameSize` the picture's natural size when known (R11: a different size is out of date);
 * `targetPlan` the floor's machines and their read sizes (guided calibration R10: a guided
 * calibration is out of date once a machine it was fitted against has moved).
 */
export function calibrationStatus(
  camera: Pick<FloorPlannerCamera, 'rtspEndpointId' | 'rtspEndpointBid' | 'calibration'>,
  {
    hasPicture,
    frameSize,
    targetPlan,
  }: {
    hasPicture: boolean;
    frameSize?: { width: number; height: number } | null;
    targetPlan?: CalibrationTargetPlan;
  },
): CalibrationStatus {
  const calibration = camera.calibration;
  const linked = camera.rtspEndpointId != null && !!camera.rtspEndpointBid;
  if (calibration) {
    if (!isValidCalibration(calibration)) {
      // Anything stored that this code cannot read, a version-1 calibration (the old flat-floor
      // model) included, is ignored and removable.
      return {
        state: 'out-of-date',
        title: 'Couldn’t read the saved calibration — recalibrate',
        sub: 'This calibration can’t be read. It is ignored until you recalibrate.',
        action: 'Recalibrate',
        disabled: !linked || !hasPicture,
        removable: true,
      };
    }
    if (!isCalibrationCurrent(camera, frameSize ?? undefined, targetPlan)) {
      const otherStream = calibration.rtspEndpointBid !== camera.rtspEndpointBid;
      const otherSize =
        !!frameSize &&
        (frameSize.width !== calibration.frameWidth ||
          frameSize.height !== calibration.frameHeight);
      return {
        state: 'out-of-date',
        title: 'Out of date — recalibrate',
        sub: otherStream
          ? `Fitted on a different stream (${calibration.rtspEndpointBid}). It is ignored until you recalibrate.`
          : otherSize || !targetPlan || !calibrationTargetsMoved(calibration, targetPlan)
            ? `Fitted on a ${calibration.frameWidth} × ${calibration.frameHeight} picture; this one is ${frameSize?.width} × ${frameSize?.height}. It is ignored until you recalibrate.`
            : 'A machine it was fitted against has moved. It is ignored until you recalibrate.',
        action: 'Recalibrate',
        disabled: !linked || !hasPicture,
        removable: true,
      };
    }
    const points = calibration.views.reduce((n, v) => n + v.pairs.length, 0);
    const { worstM, rmsPx } = storedErrors(calibration);
    const fitted = calibration.views.filter(v => v.camera).length;
    const tiles =
      calibration.views.length > 1 ? ` · ${fitted} of ${calibration.views.length} tiles` : '';
    return {
      state: 'calibrated',
      title: `Calibrated ${shortDate(calibration.fittedAt)}`,
      sub: `${points} ${sourceWords[calibration.source]}${tiles}${
        worstM === null ? '' : ` · worst ${worstM.toFixed(2)} m`
      }${rmsPx === null ? '' : ` · ${Math.round(rmsPx)} px RMS`}`,
      action: 'Recalibrate',
      disabled: !hasPicture,
      removable: true,
    };
  }
  if (!linked) {
    return {
      state: 'no-stream',
      title: 'Not calibrated',
      sub: 'Link this camera to a stream to calibrate it.',
      action: 'Calibrate',
      disabled: true,
      removable: false,
    };
  }
  if (!hasPicture) {
    return {
      state: 'no-picture',
      title: 'Not calibrated',
      sub: 'This stream has no picture yet, so it can’t be calibrated.',
      action: 'Calibrate',
      disabled: true,
      removable: false,
    };
  }
  return {
    state: 'not-calibrated',
    title: 'Not calibrated',
    sub: `Find ${MIN_GUIDED_SPOTS} machine corners on the picture so suggestions can name machines.`,
    action: 'Calibrate',
    disabled: false,
    removable: false,
  };
}

/**
 * The calibration screen's stand-in line (`onboarding-3d-plan-footprint-calibration`, R5): how many
 * machines the overlay drew at the shared stand-in size, keeping "no model" apart from "could not
 * be read" so a missing model is never reported as a broken one. Null when there are none.
 */
export function standInText({
  noModel,
  unreadable,
}: {
  noModel: number;
  unreadable: number;
}): string | null {
  const total = noModel + unreadable;
  if (total === 0) return null;
  const clauses = [
    noModel > 0 ? `${noModel} ${noModel === 1 ? 'has' : 'have'} no model` : null,
    unreadable > 0 ? `${plural(unreadable, 'model')} could not be read` : null,
  ].filter(Boolean);
  return `${plural(total, 'machine')} shown at a stand-in size: ${clauses.join(', ')}.`;
}
